import React, { useState } from "react";
import { Mail, Linkedin, MapPin, Clock, Loader2, CalendarDays } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/motion/Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { IMG } from "@/data/content";
import { SEO } from "@/data/seo";

const CALENDLY_URL = "https://calendly.com/brandon-pmnetwork/30min";
const CALENDLY_EMBED = `${CALENDLY_URL}?hide_gdpr_banner=1&background_color=f5f3ee&text_color=1f2024&primary_color=0e7490`;

const EMPTY = { full_name: "", email: "", company: "", message: "" };

const fieldCls =
  "w-full border-0 border-b border-charcoal/20 bg-transparent px-0 py-3 text-charcoal placeholder:text-charcoal/35 focus:border-teal focus:outline-none focus:ring-0 transition-colors";

function Field({ id, label, children }) {
  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-xs uppercase tracking-mega text-charcoal/70">
        {label}
      </label>
      {children}
    </div>
  );
}

function ContactForm({ onDone }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.company || !form.message) {
      setError("Please complete all fields.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      trackEvent(EVENTS.contactSubmit, { form_location: "contact_page" });
      onDone();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-8 sm:grid-cols-2" data-testid="contact-form" noValidate>
      <Field id="full_name" label="Name">
        <input id="full_name" name="name" autoComplete="name" required className={fieldCls} value={form.full_name} onChange={set("full_name")} placeholder="Jane Doe" data-testid="input-full-name" />
      </Field>
      <Field id="email" label="Work email">
        <input id="email" name="email" type="email" autoComplete="email" required className={fieldCls} value={form.email} onChange={set("email")} placeholder="jane@company.com" data-testid="input-email" />
      </Field>
      <div className="sm:col-span-2">
        <Field id="company" label="Company">
          <input id="company" name="organization" autoComplete="organization" required className={fieldCls} value={form.company} onChange={set("company")} placeholder="Company name" data-testid="input-company" />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field id="message" label="What initiative or delivery challenge are you working through?">
          <textarea
            id="message"
            name="message"
            rows={4}
            maxLength={500}
            required
            className={`${fieldCls} resize-none`}
            value={form.message}
            onChange={set("message")}
            placeholder="A stalled transformation, a PMO buildout, a program at risk…"
            data-testid="input-message"
          />
          <span className="mt-2 block text-right text-xs text-charcoal/70" aria-live="polite">{form.message.length}/500</span>
        </Field>
      </div>
      <div className="sm:col-span-2">
        {error && (
          <p role="alert" className="mb-5 border-l-2 border-red-700 pl-4 text-sm text-red-800" data-testid="contact-form-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center gap-2 bg-teal px-8 py-4 text-sm tracking-wideish text-paper transition-colors duration-500 hover:bg-charcoal disabled:opacity-60"
          data-testid="contact-submit-btn"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
          {submitting ? "Sending…" : "Submit"}
        </button>
      </div>
    </form>
  );
}

function Confirmation({ onReset }) {
  return (
    <div className="flex h-full flex-col items-start justify-center border border-teal/30 bg-paper-warm p-10 lg:p-12" role="status" data-testid="contact-success">
      <span className="h-3 w-3 rotate-45 bg-teal" />
      <h2 className="mt-6 font-serif text-3xl tracking-tightish text-charcoal">Message received.</h2>
      <p className="mt-4 max-w-md text-base leading-[1.8] text-charcoal/70">
        Thank you for reaching out. We'll review your note and respond within one business day. For
        anything urgent, email brandon@pmnetwork.org directly.
      </p>
      <button onClick={onReset} className="mt-8 text-sm tracking-wideish text-teal link-underline" data-testid="contact-reset-btn">
        Send another message
      </button>
    </div>
  );
}

function Scheduler() {
  const [showEmbed, setShowEmbed] = useState(false);
  const track = (where) => trackEvent(EVENTS.scheduling, { scheduling_location: where });
  return (
    <div className="border border-charcoal/10 bg-paper p-8 lg:p-10" data-testid="scheduling-block">
      <div className="flex items-start gap-4">
        <CalendarDays className="mt-1 h-6 w-6 shrink-0 text-teal" strokeWidth={1.25} aria-hidden="true" />
        <div>
          <h2 className="font-serif text-2xl tracking-tightish text-charcoal">Prefer to book directly?</h2>
          <p className="mt-3 text-base leading-[1.8] text-charcoal/70">
            Grab a 30-minute discovery call at a time that works for you. No pitch — we listen to what's
            going on and tell you straight whether we can help.
          </p>
        </div>
      </div>
      <div className="mt-7 flex flex-wrap items-center gap-4">
        <a
          href={CALENDLY_URL}
          target="_blank"
          rel="noreferrer"
          onClick={() => track("new_tab")}
          className="inline-flex items-center justify-center gap-2 border border-charcoal/20 px-7 py-3.5 text-sm tracking-wideish text-charcoal transition-colors duration-500 hover:border-teal hover:text-teal"
          data-testid="contact-calendly-link"
        >
          Book a 30-minute call
        </a>
        {!showEmbed && (
          <button
            type="button"
            onClick={() => { setShowEmbed(true); track("inline_embed"); }}
            className="text-sm tracking-wideish text-teal link-underline"
            data-testid="contact-calendly-inline-btn"
          >
            Or view availability here
          </button>
        )}
      </div>
      {showEmbed && (
        <div className="mt-8 overflow-hidden border border-charcoal/10" data-testid="calendly-embed">
          <iframe
            title="Schedule a 30-minute discovery call"
            src={CALENDLY_EMBED}
            width="100%"
            height="660"
            loading="lazy"
            style={{ border: 0 }}
          />
        </div>
      )}
    </div>
  );
}

const DIRECT = [
  { Icon: Mail, label: "Email", value: "brandon@pmnetwork.org", href: "mailto:brandon@pmnetwork.org", testId: "contact-email-link" },
  { Icon: Linkedin, label: "LinkedIn", value: "Project Management Network", href: "https://www.linkedin.com/company/project-management-network-pm-network/", testId: "contact-linkedin-link" },
  { Icon: MapPin, label: "Based in", value: "Dallas–Fort Worth, Texas" },
  { Icon: Clock, label: "Response time", value: "One business day" },
];

export default function Contact() {
  useSeo({ ...SEO["/contact"], image: IMG.loungeWood, path: "/contact" });
  const [done, setDone] = useState(false);

  return (
    <>
      <PageHero
        eyebrow="Contact"
        titleLines={["Let's talk about", "what your portfolio needs."]}
        subtitle="Share what's driving the initiative and we'll respond within one business day — no pitch, just a straight read on whether we're the right fit."
        image={IMG.loungeWood}
      />

      <section className="bg-paper py-24 lg:py-32">
        <div className="mx-auto max-w-8xl px-6 lg:px-10">
          <div className="grid gap-16 lg:grid-cols-12 lg:gap-24">
            <Reveal className="lg:col-span-4">
              <p className="text-xs uppercase tracking-mega text-teal-deep">Direct</p>
              <ul className="mt-8 space-y-8">
                {DIRECT.map(({ Icon, label, value, href, testId }) => {
                  const inner = (
                    <>
                      <Icon className="mt-1 h-5 w-5 shrink-0 text-teal" strokeWidth={1.25} aria-hidden="true" />
                      <span>
                        <span className="block text-xs uppercase tracking-mega text-charcoal/70">{label}</span>
                        <span className="mt-1 block text-charcoal transition-colors group-hover:text-teal">{value}</span>
                      </span>
                    </>
                  );
                  return (
                    <li key={label}>
                      {href ? (
                        <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="group flex items-start gap-4" data-testid={testId}>
                          {inner}
                        </a>
                      ) : (
                        <div className="flex items-start gap-4">{inner}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </Reveal>

            <div className="space-y-10 lg:col-span-8">
              <Reveal delay={0.1}>
                <p className="mb-10 text-xs uppercase tracking-mega text-teal-deep">Tell us what you're working through</p>
                {done ? <Confirmation onReset={() => setDone(false)} /> : <ContactForm onDone={() => setDone(true)} />}
              </Reveal>
              <Reveal delay={0.15}>
                <Scheduler />
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
